import React, { ChangeEvent, useEffect, useState } from 'react'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { AppMainView, AppView } from '../../../components/AppView'
import AppHeader from '../../../components/AppView/Header'
import { HelpLinkMenu } from '../../../components/GrDropdown/HelpLinkMenu/HelpLinkMenu'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { Checkbox, Col, Input, message, Modal, Row, Table, Tooltip, Typography } from 'antd-v4'
import { CopyOutlined, InfoCircleFilled } from '@ant-design/icons'
import { BlazeButton } from '../../../components/CustomButtons/BlazeButton'
import { ColumnsType } from 'antd-v4/lib/table/interface'
import { BlazeCRSALocationSelector, ICRSALocations } from './BlazeCRSALocationSelector'
import {
  createHashKey,
  deleteHashKey,
  ICreateHashKeyRequestContract,
  ITenantHashKeyPathParams,
  ITenantPathParams,
  IUpdateHashKeyRequestContract,
  updateHashKey,
} from '@getgreenline/ocs'
import {
  API,
  ICompanyWithMetaDataContract,
  MetaDataType,
  MetaKeys,
} from '@getgreenline/homi-shared'
import endpoints from '../../../constants/Endpoints'
import { GrRegex, parseErrorMsg } from '../../../utilities/helpers'
import { ILocationContract } from '@getgreenline/locations/build/models'
import { HelpLinks } from '../../../constants/ExternalLinks'
const { Text } = Typography

interface InjectedProps {
  currentUserStore?: CurrentUserStore
  currentCompanyStore?: CurrentCompanyStore
}

const OCSComplianceSettingsComponent = ({
  currentCompanyStore,
  currentUserStore,
}: InjectedProps) => {
  /* State Variables */
  const [selectedCRSA, setSelectedCRSA] = useState<ICRSALocations | undefined>()
  const [locationsWithCRSA, setLocationsWithCRSA] = useState<ILocationContract[]>([])
  const [hashKeys, setHashKeys] = useState<ICreateHashKeyRequestContract[]>([])
  const [newHashKey, setNewHashKey] = useState<string | undefined>()
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [isCreating, setIsCreating] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [recordToDelete, setRecordToDelete] = useState<ICreateHashKeyRequestContract | null>(null)
  const [companyWithMetaData, setCompanyWithMetaData] =
    useState<ICompanyWithMetaDataContract<MetaDataType> | undefined>()
  const [showVirtualLocationCheckboxes, setShowVirtualLocationCheckboxes] = useState<boolean>(false)

  /* Constants */
  const currentUserID = currentUserStore?.currentUser?.id
  if (!currentUserID) throw new Error('unknown user, please authenticate, and try again')

  const tenantPathParams: ITenantPathParams = {
    gateway: endpoints.AUTHZ_GATEWAY_URL,
    tenantId: `${currentCompanyStore?.company?.id}`,
  }

  const isUserGreenlineAdmin = currentUserStore?.isAdmin

  /* Lifecycle Hooks */
  useEffect(() => {
    setHashKeys(getHashKeys())
    setLocationsWithCRSA(getLocationsWithCRSA(currentCompanyStore?.locations || []))

    if (!companyWithMetaData) {
      setVirtualLocationCheckboxes()
    }
  }, [currentCompanyStore?.globalHashKeys, showVirtualLocationCheckboxes])

  /* Actions */
  const clearFormData = () => {
    setSelectedCRSA(undefined)
    setNewHashKey(undefined)
  }

  const getLocationsWithCRSA = (locations: ILocationContract[]) => {
    const isValidCRSALocationFilter = (location: ILocationContract) =>
      GrRegex.isValidCRSA(location.storeId || '')
    return locations.filter(isValidCRSALocationFilter)
  }

  const getHashKeys = (): ICreateHashKeyRequestContract[] => {
    return currentCompanyStore?.globalHashKeys || []
  }

  const addItemToGlobaHashKeys = (hashKey: ICreateHashKeyRequestContract) => {
    if (!currentCompanyStore?.globalHashKeys) return
    currentCompanyStore.globalHashKeys = [...currentCompanyStore.globalHashKeys, hashKey]
  }

  const removeItemFromGlobaHashKeys = (hashKey: ICreateHashKeyRequestContract) => {
    if (!currentCompanyStore?.globalHashKeys) return
    currentCompanyStore.globalHashKeys = currentCompanyStore.globalHashKeys.filter(
      (globalHashKeys) => {
        return globalHashKeys.hashKey !== hashKey.hashKey
      },
    )
  }

  const setVirtualLocationCheckboxes = async () => {
    const companyId = currentCompanyStore?.company?.id
    if (!companyId) throw new Error('No company ID')

    try {
      const companyWithMetaData = await API.getCompanyById(companyId, true)

      setCompanyWithMetaData(companyWithMetaData)

      const showVirtualLocationCheckboxes =
        companyWithMetaData.metaData[MetaKeys.hasVirtualLocation]

      setShowVirtualLocationCheckboxes(showVirtualLocationCheckboxes === true ?? false)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while retrieving company data')
      console.error(error)
    }
  }

  const updateVirtualLocation = async (hashKey: ICreateHashKeyRequestContract) => {
    const params: ITenantHashKeyPathParams = {
      ...tenantPathParams,
      hashKey: hashKey.hashKey,
    }

    const payload: IUpdateHashKeyRequestContract = {
      ...hashKey,
      updateBy: currentUserID ? `userId-${currentUserID}` : '',
      isVirtual: !hashKey.isVirtual,
    }

    try {
      await updateHashKey(params, payload)
      message.info('Virtual location was successfully updated')
      await currentCompanyStore?.getGlobalHashKeys()
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while updating virtual location')
      console.error(error)
    }
  }

  const addHashKeyHandler = async () => {
    if (!selectedCRSA || !newHashKey) {
      message.error('both hash key and CRSA must be provided')
      return
    }
    try {
      setIsCreating(true)
      const payload: ICreateHashKeyRequestContract = {
        retailerCRSA: selectedCRSA.crsa,
        retailerIds: selectedCRSA.locations.map((child) => `${child.id}`),
        hashKey: newHashKey,
        updateBy: currentUserID ? `userId-${currentUserID}` : '',
      }
      await createHashKey(tenantPathParams, payload)
      message.info('Hashkey entry was successfully created')
      clearFormData()
      addItemToGlobaHashKeys(payload)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while retrieving hash key entries')
    } finally {
      setIsCreating(false)
    }
  }

  const removeHashKeyHandler = async (hashKey: ICreateHashKeyRequestContract) => {
    setIsDeleting(true)
    const targetHashKey = hashKey.hashKey
    await deleteHashKey({
      ...tenantPathParams,
      hashKey: targetHashKey,
    })
    removeItemFromGlobaHashKeys(hashKey)
    setIsDeleting(false)
    setShowDeleteModal(false)
  }

  /* Custom Table Column Templates */
  const hashkeyColumnTransformer = (_: unknown, item: ICreateHashKeyRequestContract) => (
    <Text
      copyable={{
        text: item.hashKey,
        icon: <CopyOutlined />,
        tooltips: ['Copy', 'Copied'],
      }}
      key={item.hashKey}
      style={{ width: '300px', wordWrap: 'break-word', display: 'inline-block' }}
    >
      {item.hashKey}
    </Text>
  )

  const licenceIdColumnTemplate = (_: unknown, item: ICreateHashKeyRequestContract) => (
    <span key={item.hashKey}>{item.retailerCRSA}</span>
  )

  const locationColumnTemplate = (_: unknown, item: ICreateHashKeyRequestContract) => {
    const linkedLocations = locationsWithCRSA.filter((location) =>
      item.retailerIds.includes(`${location.id}`),
    )
    return (
      <ul key={item.hashKey} style={{ padding: 0, margin: 0 }}>
        {linkedLocations.map((location) => (
          <li key={location.id}>{location.name}</li>
        ))}
      </ul>
    )
  }

  const optionsColumnTemplate = (_: unknown, item: ICreateHashKeyRequestContract) => {
    return (
      <>
        <BlazeButton
          key='delete'
          blazeButtonType='danger'
          buttonProps={{
            type: 'link',
            onClick: () => {
              setShowDeleteModal(true)
              setRecordToDelete(item)
            },
          }}
          titleProps={{ title: 'Delete' }}
        />
      </>
    )
  }

  const virtualLocationColumnTemplate = (_: unknown, item: ICreateHashKeyRequestContract) => {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Checkbox
          disabled={!isUserGreenlineAdmin}
          defaultChecked={item.isVirtual === true}
          onChange={() => updateVirtualLocation(item)}
        />
      </div>
    )
  }

  const columns: ColumnsType<ICreateHashKeyRequestContract> = [
    {
      dataIndex: 'hashKey',
      title: 'Hash Key',
      key: 'hashKey',
      render: hashkeyColumnTransformer,
      align: 'left',
      width: '25%',
    },
    {
      dataIndex: 'licenceId',
      title: 'Licence ID',
      key: 'licenceId',
      render: licenceIdColumnTemplate,
      align: 'left',
      width: '15%',
    },
    {
      dataIndex: 'locations',
      title: 'Locations',
      key: 'locations',
      render: locationColumnTemplate,
      align: 'left',
      width: '35%',
    },
    ...(showVirtualLocationCheckboxes
      ? [
          {
            dataIndex: 'virtualLocation',
            title: (
              <span>
                <span className='mr-2'>Virtual Location</span>
                <Tooltip
                  placement='right'
                  overlayStyle={{ minWidth: 'fit-content' }}
                  title={
                    <div>
                      Check this box if the location is a virtual location and its data should not
                      be submitted to OCS.
                    </div>
                  }
                >
                  <InfoCircleFilled />
                </Tooltip>
              </span>
            ),
            key: 'virtualLocation',
            render: virtualLocationColumnTemplate,
            align: 'center' as const,
            width: '15%',
          },
        ]
      : []),
    {
      dataIndex: 'options',
      title: 'Options',
      key: 'options',
      render: optionsColumnTemplate,
      align: 'left',
      width: '10%',
    },
  ]

  return (
    <AppView column>
      <AppHeader title='Compliance Settings'>
        <div className='d-flex'>
          <HelpLinkMenu
            helpLinkMenus={{
              href: HelpLinks.REPORTS_COMPLIANCE,
              title: 'Ontario Compliance',
            }}
          />
        </div>
      </AppHeader>
      <AppMainView className='background-grey intercom-pb-2'>
        <div className='white-rounded-container pl-3 pr-3 pb-3'>
          <Row gutter={10} className='pb-3'>
            <Col lg={12} sm={12}>
              <Input
                maxLength={64}
                className='mt-2 mb-2'
                value={newHashKey}
                placeholder='Enter your hash key here'
                allowClear={true}
                onChange={(value: ChangeEvent<HTMLInputElement>) =>
                  setNewHashKey(value.target.value)
                }
              />
            </Col>
            <Col lg={12} sm={12}>
              <div className='mt-2'>
                <BlazeCRSALocationSelector
                  value={selectedCRSA?.crsa}
                  locations={locationsWithCRSA}
                  onSelect={({ crsaLocation }) => setSelectedCRSA(crsaLocation)}
                  disabledCRSALocations={hashKeys.map((hashKey) => hashKey?.retailerCRSA)}
                />
                <BlazeButton
                  buttonProps={{
                    disabled: selectedCRSA && newHashKey ? false : true,
                    className: 'mb-2 ml-2',
                    type: 'primary',
                    onClick: () => addHashKeyHandler(),
                    loading: isCreating,
                  }}
                  titleProps={{ title: 'Add' }}
                />
              </div>
            </Col>
          </Row>
          <Table
            pagination={false}
            dataSource={hashKeys}
            columns={columns}
            className='no-background-table intercom-pb-3'
            scroll={{ x: '400px' }}
          />
          {recordToDelete && (
            <Modal
              visible={showDeleteModal}
              okText='Delete'
              onCancel={() => setShowDeleteModal(false)}
              confirmLoading={isDeleting}
              okButtonProps={{ disabled: isDeleting }}
              title={<p className='font-weight-bold mb-0'>Delete hash key entry</p>}
              className='text-danger'
              footer={[
                <BlazeButton
                  key='cancel'
                  buttonProps={{
                    className: 'mb-2 ml-2',
                    onClick: () => setShowDeleteModal(false),
                  }}
                  titleProps={{ title: 'Cancel' }}
                />,
                <BlazeButton
                  key='delete'
                  buttonProps={{
                    className: 'mb-2 ml-2',
                    type: 'danger',
                    onClick: () => removeHashKeyHandler(recordToDelete),
                    loading: isDeleting,
                  }}
                  titleProps={{ title: 'Delete' }}
                />,
              ]}
            >
              Are you sure you want to delete hash key
              <b>{' ' + recordToDelete.hashKey + ' '}</b>
              assigned to
              <b>{' ' + recordToDelete.retailerCRSA + ' '}</b>?
            </Modal>
          )}
        </div>
      </AppMainView>
    </AppView>
  )
}

export const DashboardOCSComplianceSettings = inject(
  'currentUserStore',
  'currentCompanyStore',
)(observer(OCSComplianceSettingsComponent))
